<script>
  import { onMount } from "svelte"
  import { flip } from "svelte/animate"

  // The whole dashboard card grid: the two sections, their search box and the
  // create-and-log escape hatch. Everything a card says is server-decided and
  // arrives in props; this island chooses which cards exist and in what order,
  // which is why the sorting the controller used to do lives here.
  // flashQuickLog is the log a redirect arrived here with -- a trigger URL, or
  // a quick log posted without JavaScript -- in the shape a log made on this
  // page answers with, so both raise the same toast.
  let { recurring = [], adhoc = [], createAndLogPath, flashQuickLog = null } = $props()

  // Logging without leaving the page means a card outlives the request that
  // drew it, so props only seed these. A quick log swaps one entry for the
  // card the server sends back, and the sort below moves it.
  // svelte-ignore state_referenced_locally -- islands remount per visit; props seed state once
  let recurringEvents = $state([...recurring])
  // svelte-ignore state_referenced_locally
  let adhocEvents = $state([...adhoc])

  // The open log modal: the card it belongs to and the form the server sent
  // for it, or null for no modal. The form is server-rendered HTML — the same
  // partial the trigger scan page uses — so the field inputs have exactly one
  // renderer, in Rails.
  let modal = $state(null)
  let modalBody = $state()

  let input = $state()
  let query = $state("")

  const term = $derived(query.trim())
  const needle = $derived(term.toLowerCase())

  const recurringCards = $derived(matching(recurringEvents).sort(byDueDate))
  const adhocCards = $derived(matching(adhocEvents).sort(byLastLogged))
  const nothingMatched = $derived(
    term !== "" && recurringCards.length === 0 && adhocCards.length === 0
  )

  // Read at mount: Turbo replaces the body and the islands remount with it, so
  // this is always the token of the page currently on screen.
  const csrfToken = document.querySelector("meta[name='csrf-token']")?.content ?? ""

  const reduceMotion =
    typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches
  const flipDuration = reduceMotion ? 0 : 300

  const severityClasses = {
    danger: "has-text-danger has-text-weight-semibold",
    warning: "has-text-warning",
    muted: "has-text-grey"
  }

  function matching(events) {
    if (!needle) return [...events]
    return events.filter((event) => event.name.toLowerCase().includes(needle))
  }

  // Soonest first. An event nobody has logged yet has no due date at all, and
  // goes after every event that has one.
  function byDueDate(a, b) {
    if (a.daysUntilDue === null) return b.daysUntilDue === null ? 0 : 1
    if (b.daysUntilDue === null) return -1
    return a.daysUntilDue - b.daysUntilDue
  }

  function byLastLogged(a, b) {
    if (a.lastOccurredAt === null) return b.lastOccurredAt === null ? 0 : 1
    if (b.lastOccurredAt === null) return -1
    return Date.parse(b.lastOccurredAt) - Date.parse(a.lastOccurredAt)
  }

  function clear() {
    query = ""
    input.focus()
  }

  async function requestJson(path, method) {
    const response = await fetch(path, {
      method,
      credentials: "same-origin",
      headers: { Accept: "application/json", "X-CSRF-Token": csrfToken }
    })

    if (!response.ok) throw new Error(`${method} ${path} answered ${response.status}`)

    return response.json()
  }

  // Both actions hand back the event's card as the server now reads it, which
  // takes the place of the one on screen; the sort then moves it.
  function replaceCard(card) {
    for (const cards of [recurringEvents, adhocEvents]) {
      const index = cards.findIndex((existing) => existing.id === card.id)
      if (index !== -1) {
        cards[index] = card
        return
      }
    }
  }

  function reportFailure(message) {
    document.dispatchEvent(new CustomEvent("toast", { detail: { type: "alert", message } }))
  }

  // A log says so in an overlay rather than on the page, so nothing a visitor
  // is looking at moves when they log something. It stays up long enough to
  // undo, which is the whole reason to notice it.
  function reportQuickLog(quickLog) {
    document.dispatchEvent(
      new CustomEvent("toast", {
        detail: {
          type: "notice",
          message: quickLog.message,
          duration: 10000,
          actions: [
            { label: "Edit", href: quickLog.editPath },
            {
              label: "Undo",
              onClick: () => {
                if (!confirm("Undo this log?")) return false
                undo(quickLog)
              }
            }
          ]
        }
      })
    )
  }

  // onMount rather than $effect: raising a toast runs the Toasts island's own
  // state through it synchronously, which a tracking effect would take as a
  // dependency and re-run on -- raising another toast, forever.
  onMount(() => {
    if (flashQuickLog) reportQuickLog(flashQuickLog)
  })

  async function logNow(submit, card) {
    submit.preventDefault()

    try {
      const logged = await requestJson(card.quickLogPath, "POST")
      replaceCard(logged.card)
      reportQuickLog(logged.quickLog)
    } catch {
      reportFailure(`Could not log ${card.name}. Reload the page and try again.`)
    }
  }

  // An event that insists on details has no unattended log, so the button
  // opens a modal of exactly those fields instead. Without JavaScript the
  // anchor is what it looks like — a link to the full log form.
  async function openModal(click, card) {
    click.preventDefault()
    modal = { card, html: null }

    try {
      const response = await fetch(card.fieldsPath, {
        credentials: "same-origin",
        headers: { Accept: "text/html", "X-CSRF-Token": csrfToken }
      })

      if (!response.ok) throw new Error(`GET ${card.fieldsPath} answered ${response.status}`)

      modal = { card, html: await response.text() }
    } catch {
      modal = null
      reportFailure(`Could not open ${card.name}. Reload the page and try again.`)
    }
  }

  function closeModal() {
    modal = null
  }

  // One listener over the fetched form, so the server may replace it as often
  // as the visitor gets it wrong without anything needing to be re-bound.
  async function submitModal(submit) {
    const form = submit.target
    if (!(form instanceof HTMLFormElement)) return

    submit.preventDefault()
    const card = modal.card

    try {
      const response = await fetch(form.action, {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-CSRF-Token": csrfToken },
        body: new FormData(form)
      })

      const answer = await response.json()

      if (!response.ok) {
        if (!answer.formHtml) throw new Error(`POST ${form.action} answered ${response.status}`)

        modal = { card, html: answer.formHtml }
        return
      }

      replaceCard(answer.card)
      reportQuickLog(answer.quickLog)
      modal = null
    } catch {
      reportFailure(`Could not log ${card.name}. Reload the page and try again.`)
    }
  }

  // Whatever the form asks for first is what the visitor came to type.
  $effect(() => {
    if (modal?.html && modalBody) {
      modalBody.querySelector("input:not([type=hidden]), select, textarea")?.focus()
    }
  })

  async function undo(quickLog) {
    try {
      const undone = await requestJson(quickLog.undoPath, "DELETE")
      replaceCard(undone.card)
    } catch {
      reportFailure("Could not undo that log. Reload the page and try again.")
    }
  }

  // "/" is only a shortcut while the visitor is not already typing somewhere.
  function editingText() {
    const tag = document.activeElement?.tagName
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT"
  }

  $effect(() => {
    function handleKeydown(event) {
      // While the modal is up it is the only thing on screen the keyboard
      // talks to; the search box behind it keeps its shortcut for later.
      if (modal) {
        if (event.key === "Escape") closeModal()
        return
      }

      if (event.key === "/" && !editingText()) {
        event.preventDefault()
        input.focus()
      } else if (event.key === "Escape" && document.activeElement === input) {
        query = ""
        input.blur()
      }
    }

    document.addEventListener("keydown", handleKeydown)
    return () => document.removeEventListener("keydown", handleKeydown)
  })
</script>

<div class="search-container">
  <input
    type="text"
    id="event-search"
    class="input search-input"
    placeholder="Search events…"
    autocomplete="off"
    autocapitalize="off"
    autocorrect="off"
    spellcheck="false"
    bind:value={query}
    bind:this={input}
  />
  <button
    type="button"
    id="search-clear"
    class="delete"
    aria-label="Clear search"
    hidden={!query}
    onclick={clear}
  ></button>
</div>

{#snippet section(heading, cards)}
  {#if cards.length > 0}
    <div class="block">
      <h2 class="title is-5">{heading}</h2>
      <div class="fixed-grid has-2-cols-mobile has-3-cols-tablet has-4-cols-desktop">
        <div class="grid">
          {#each cards as card (card.id)}
            <div class="cell" animate:flip={{ duration: flipDuration }}>
              <div class="box">
                <a class="is-block has-text-weight-semibold" href={card.logPath}>{card.name}</a>

                {#if card.recurring}
                  <p class="is-size-7 has-text-grey mt-1">
                    Every {card.recurrenceDays} {card.recurrenceDays === 1 ? "day" : "days"}
                  </p>
                {/if}

                {#if card.dueLabel}
                  <p class="is-size-7 mt-1">
                    <span class={severityClasses[card.dueSeverity]}>{card.dueLabel}</span>
                  </p>
                {/if}

                <p class="is-size-7 has-text-grey mt-1">
                  {card.lastOccurredLabel ? `Last: ${card.lastOccurredLabel}` : "Never logged"}
                </p>

                {#if card.quickLoggable}
                  <!-- The form is what a page without JavaScript posts; with it,
                       the same path answers in JSON and nothing navigates. -->
                  <form
                    class="log-action"
                    action={card.quickLogPath}
                    method="post"
                    onsubmit={(submit) => logNow(submit, card)}
                  >
                    <input type="hidden" name="authenticity_token" value={csrfToken} />
                    <button type="submit" class="button is-small is-primary">Log Now</button>
                  </form>
                {:else}
                  <!-- Nothing can log this event unattended, so the button
                       asks for what it needs first. With no JavaScript the
                       link goes where it points: the full log form. -->
                  <div class="log-action">
                    <a
                      class="button is-small is-primary"
                      href={card.logPath}
                      onclick={(click) => openModal(click, card)}>Log Now</a
                    >
                  </div>
                {/if}
              </div>
            </div>
          {/each}
        </div>
      </div>
    </div>
  {/if}
{/snippet}

{@render section("Recurring", recurringCards)}
{@render section("Ad Hoc", adhocCards)}

{#if modal}
  <div class="modal is-active" data-log-modal>
    <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
    <div class="modal-background" onclick={closeModal}></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">{modal.card.name}</p>
        <button type="button" class="delete" aria-label="close" onclick={closeModal}></button>
      </header>
      <section class="modal-card-body">
        {#if modal.html}
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <div bind:this={modalBody} onsubmit={submitModal}>
            {@html modal.html}
          </div>
        {:else}
          <p class="has-text-grey">Loading…</p>
        {/if}
      </section>
    </div>
  </div>
{/if}

{#if nothingMatched}
  <div class="notification">
    <p>No events match "{term}".</p>
    <form class="mt-3" action={createAndLogPath} method="post">
      <input type="hidden" name="authenticity_token" value={csrfToken} />
      <input type="hidden" name="name" value={term} />
      <input type="submit" class="button is-primary" value={"Create & Log"} />
    </form>
  </div>
{/if}

<style>
  .search-container {
    position: relative;
    margin-bottom: 1.5rem;
  }

  /* Room for the clear button overlaid on the input's right edge. */
  .search-input {
    padding-right: 2.5rem;
  }

  #search-clear {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
  }
</style>
